import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  pipe,
  Subject,
  takeUntil,
} from 'rxjs';

const filterAndCastToT = <T>() =>
  pipe(
    filter((v: T | null) => v !== null),
    map((v) => v as T)
  );

// Tento "dirty fix" je zde kvůli problému s BehaviorSubject
export class StateSubject<T> extends BehaviorSubject<T> {
  public readonly syncState = this.asObservable().pipe(map(() => this.value)) as Observable<T>;
}

@Injectable()
export class ObservableState<T extends Record<string, unknown>> implements OnDestroy {
  private readonly notInitializedError = 'Observable state není inicializován, zavolejte metodu initialize()';
  private readonly destroy$$ = new Subject<void>();
  private readonly state$$ = new StateSubject<T | null>(null);
  private silentMode = false; // Přidáváme příznak pro "tichý" režim

  private readonly connectedObservables: Partial<{
    [P in keyof T]: Observable<T[P]>;
  }> = {};

  /**
   * Vrací celý stav jako observable.
   * Použijte pouze, pokud chcete být informováni o každé aktualizaci. Pro lepší optimalizaci
   * použijte metodu onlySelectWhen(), kde můžete předat klíče, kdy má být notifikace provedena.
   */
  public readonly state$ = this.state$$.syncState.pipe(
    filterAndCastToT<T>(),
    distinctUntilChanged((previous: T, current: T) =>
      Object.keys(current).every((key: string) => current[key as keyof T] === previous[key as keyof T])
    ),
    takeUntil(this.destroy$$)
  );

  /**
   * Získání aktuálního stavu jako snapshot. Tato metoda je užitečná, pokud chcete získat
   * stav v funkcích, kde není potřeba použít withLatestFrom.
   */
  public get snapshot(): T {
    if (!this.state$$.value) {
      throw new Error(this.notInitializedError);
    }
    return this.state$$.value as T;
  }

  /**
   * Inicializace observable stavu. Stav vždy potřebuje počáteční hodnotu.
   * Můžete předat volitelný @InputState(), který automaticky naplní stav správnými hodnotami.
   * @param state
   * @param inputState$
   */
  public initialize(state: T, inputState$?: Observable<Partial<T>>): void {
    this.state$$.next(state); // nastavení počátečního stavu
    inputState$?.pipe(takeUntil(this.destroy$$)).subscribe((res: Partial<T>) => this.patch(res));
  }

  /**
   * Připojení více observables k části stavu. Předá se objekt s klíči, které patří do stavu, a jejich observables.
   * @param object
   */
  public connect(object: Partial<{ [P in keyof T]: Observable<T[P]> }>): void {
    Object.keys(object).forEach((key) => {
      const typedKey = key as keyof T;
      this.connectedObservables[typedKey] = object[typedKey];
      object[typedKey]?.pipe(takeUntil(this.destroy$$)).subscribe((v: Partial<T>[keyof Partial<T>]) => {
        this.patch({ [typedKey]: v } as Partial<T>);
      });
    });
  }

  /**
   * Vrací celý stav, když některá z vlastností odpovídající předaným klíčům se změní.
   * @param keys
   */
  public onlySelectWhen(keys: (keyof T)[]): Observable<T> {
    return this.state$$.syncState.pipe(
      filterAndCastToT<T>(),
      filter(() => !this.silentMode), // Ignorujeme změny, když je aktivní tichý režim
      distinctUntilChanged((previous: T, current: T) =>
        keys.every((key: keyof T) => current[key as keyof T] === previous[key as keyof T])
      ),
      takeUntil(this.destroy$$)
    );
  }

  /**
   * Vrací observable konkrétně vybrané části stavu podle klíče.
   * @param key
   */
  public select<P extends keyof T>(key: P): Observable<T[P]> {
    return this.onlySelectWhen([key]).pipe(map((state) => state[key]));
  }

  /**
   * Úprava části stavu. Prochází všechny vlastnosti předaného objektu a stav aktualizuje pouze jednou.
   * @param object
   * @param silent Pokud je true, změna stavu nevyvolá notifikaci
   */
  public patch(object: Partial<T>, silent = false): void {
    if (!this.state$$.value) {
      throw new Error(this.notInitializedError);
    }

    if (silent) {
      this.silentMode = true;
    }

    let newState: T = { ...this.state$$.value };
    Object.keys(object).forEach((key) => {
      const typedKey = key as keyof T;
      newState = { ...newState, [typedKey]: object[typedKey] };
    });

    this.state$$.next(newState); // Aktualizace stavu

    if (silent) {
      setTimeout(() => {
        this.silentMode = false;
      });
    }
  }

  /**
   * Vybere části stavu a vytvoří objekt, který má observables pro každý předaný klíč.
   * @param keys
   */
  public pick<P>(keys: (keyof T)[]): Partial<{ [P in keyof T]: Observable<T[P]> }> {
    const returnObj: Partial<{ [P in keyof T]: Observable<T[P]> }> = {};
    keys.forEach((key: keyof T) => {
      returnObj[key] = this.onlySelectWhen([key]).pipe(map((state: T) => state[key]));
    });
    return returnObj;
  }

  /**
   * Zpracování chyb pro dané Observable.
   * @param observable
   */
  public handleErrors<U>(observable: Observable<U>, result: U = {} as U): Observable<U | (U & { error: boolean })> {
    return observable.pipe(
      catchError((error) => {
        console.error('Došlo k chybě:', error);
        // Zde ošetřete chybu. Například můžete aktualizovat stav s chybou.
        return of({ ...result, error: true }); // Pokud chcete chybu ošetřit výše v řetězci, znovu vyvolejte chybu.
      })
    );
  }

  /**
   * Spustí observable pro daný klíč znovu.
   * @param key
   */
  public trigger(key: keyof T): void {
    const observable = this.connectedObservables[key];
    if (observable) {
      observable.pipe(takeUntil(this.destroy$$)).subscribe((v) => {
        this.patch({ [key]: v } as Partial<T>);
      });
    } else {
      console.warn(`Observable pro klíč ${String(key)} není připojen.`);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }
}
